import { type UserDisplayItem } from "../user/user-display-utils";
import { UserDisplayHelper } from "../user/user-display-utils";
import { CommonUtils } from "../util/common-utils";
import { WysiwygMentionRender } from "../wysiwyg/wysiwyg-mention/wysiwyg-mention-render";
import { MentionControllerBase, type MentionMatch } from "../wysiwyg/wysiwyg-mention/wysiwyg-mention-handler";
import { TextareaMentionListener } from "./comment-mention-listener";
import { commentForm } from "./comment-form";

const controllerRegistry = new WeakMap<HTMLTextAreaElement, TextareaMentionController>();

class TextareaMentionController extends MentionControllerBase {
    private readonly mentionMap = new Map<string, string>();

    public constructor(private readonly textarea: HTMLTextAreaElement) {
        super(textarea);
    }

    public override bind(): void {
        TextareaMentionListener.bind(this.textarea, this.dropdownEl, this);
    }

    protected override resolveMentionMatch(): MentionMatch | null {
        const cursor = this.textarea.selectionStart ?? 0;
        const contextStart = Math.max(0, cursor - 64);
        const contextText = this.textarea.value.substring(contextStart, cursor);
        const match = contextText.match(/(?:^|\s)@([\p{L}\p{N}_\-.]{0,30})$/u);
        if (!match) {
            return null;
        }

        const query = String(match[1] ?? "").trim();
        const fullMatch = match[0];
        const hasLeadingSpace = fullMatch.length > 0 && /^\s/.test(fullMatch);
        const atOffset = hasLeadingSpace ? 1 : 0;
        const startIndex = contextStart + contextText.length - fullMatch.length + atOffset;
        if (startIndex < 0) {
            return null;
        }

        return { query, startIndex, cursorIndex: cursor };
    }

    protected override doInsertMention(user: UserDisplayItem, match: MentionMatch): void {
        const displayNameWithAt = UserDisplayHelper.ensureAtPrefix(user.displayName);
        const plainText = displayNameWithAt + " ";
        const before = this.textarea.value.substring(0, match.startIndex);
        const after = this.textarea.value.substring(match.cursorIndex);
        this.textarea.value = before + plainText + after;
        const newCursor = match.startIndex + plainText.length;
        this.textarea.setSelectionRange(newCursor, newCursor);

        const nameKey = displayNameWithAt.replace(/^@/, "");
        this.mentionMap.set(user.id, nameKey);

        this.textarea.dispatchEvent(new Event("input", { bubbles: true }));
    }

    protected override doPositionDropdown(match: MentionMatch): void {
        WysiwygMentionRender.positionDropdownAtCaret(this.dropdownEl, this.textarea, match.cursorIndex);
    }

    protected override isOwnEditorEl(target: Node): boolean {
        return target === this.textarea;
    }

    public buildHtmlContent(): string {
        let content = this.textarea.value;
        for (const [userId, nameKey] of this.mentionMap) {
            if (!content.includes(`@${nameKey}`)) {
                continue;
            }
            const escapedName = nameKey.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const anchor = `<a href="/users/${CommonUtils.escapeHtmlAttribute(userId)}" class="user-mention-link" data-user-id="${CommonUtils.escapeHtmlAttribute(userId)}">@${CommonUtils.escapeHtml(nameKey)}</a>`;
            content = content.replace(new RegExp(`@${escapedName}(?=[\\s,.!?]|$)`, "gu"), anchor);
        }
        return content;
    }
}

export class TextareaMentionSetup {
    public static init(textarea: HTMLTextAreaElement | null): void {
        if (!(textarea instanceof HTMLTextAreaElement)) {
            return;
        }
        if (textarea.dataset.mentionInitialized === "1") {
            return;
        }

        const controller = new TextareaMentionController(textarea);
        controller.bind();
        controllerRegistry.set(textarea, controller);
        textarea.dataset.mentionInitialized = "1";
    }

    public static initAll(root: ParentNode = document): void {
        commentForm.getAllMentionTargetTextareas(root).forEach((textarea) => {
            TextareaMentionSetup.init(textarea);
        });
    }

    public static getHtmlContent(textarea: HTMLTextAreaElement): string {
        const controller = controllerRegistry.get(textarea);
        return controller?.buildHtmlContent() ?? textarea.value;
    }
}
